import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
    OverlayDrawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    DrawerFooter,
} from "@fluentui/react-drawer";
import {
    Badge,
    Button,
    Text,
    makeStyles,
    tokens,
    Spinner,
    Radio,
    Tooltip,
} from "@fluentui/react-components";
import {
    ArrowUp16Regular,
    ArrowDown16Regular,
    ArrowRight16Regular,
    ArrowSort16Regular,
    Building16Regular,
    Dismiss16Regular,
    Link16Regular,
    PersonAdd16Regular,
} from "@fluentui/react-icons";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import GlobalTableSearch from "../ManageCustomers/GlobalTableSearch";
import SfUserPickerTable from "./SfUserPickerTable";

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
    topBar: {
        padding: "8px 16px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: "6px",
    },
    topBarLabel: {
        fontSize: "12px",
        color: tokens.colorNeutralForeground3,
        lineHeight: "1.3",
    },
    tableScroll: {
        flex: 1,
        overflowY: "auto",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        background: tokens.colorNeutralBackground1,

        "& th, & td": {
            padding: "6px 8px",
            borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
            textAlign: "left",
        },

        "& thead th": {
            background: tokens.colorNeutralBackground2,
            fontWeight: 600,
        },
    },
    tableHeader: {
        position: "sticky",
        top: 0,
        zIndex: 2,
    },
    radioCell: {
        display: "flex",
        alignItems: "center",
        margin: 0,
    },
    footerActions: {
        display: "flex",
        gap: "8px",
        justifyContent: "flex-end",
        width: "100%",
    },

    // ── Empty / Loading states ─────────────────────────────────────────────
    stateCenter: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "12px",
        padding: "56px 24px",
        textAlign: "center",
    },
    stateIcon: {
        fontSize: "40px",
        color: tokens.colorNeutralForeground3,
        opacity: 0.5,
    },
    stateTitle: {
        fontWeight: 600,
        fontSize: "15px",
        color: tokens.colorNeutralForeground1,
    },
    stateDesc: {
        fontSize: "13px",
        color: tokens.colorNeutralForeground3,
        maxWidth: "280px",
    },

    // ── Row hover ──────────────────────────────────────────────────────────
    row: {
        cursor: "pointer",
        ":hover td": {
            backgroundColor: tokens.colorSubtleBackgroundHover,
        },
    },
    rowSelected: {
        "& td": {
            backgroundColor: tokens.colorSubtleBackgroundSelected,
        },
    },

    // ── Step indicator ────────────────────────────────────────────────────
    stepBar: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        marginBottom: "2px",
    },
    stepItem: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "12px",
        color: tokens.colorNeutralForeground3,
    },
    stepItemActive: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "12px",
        fontWeight: 600,
        color: tokens.colorBrandForeground1,
    },
    stepItemDone: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "12px",
        color: tokens.colorNeutralForeground2,
    },
    stepDot: {
        width: "16px",
        height: "16px",
        borderRadius: "50%",
        border: `1.5px solid ${tokens.colorNeutralStroke1}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "10px",
        fontWeight: 600,
        flexShrink: 0,
    },
    stepDotActive: {
        width: "16px",
        height: "16px",
        borderRadius: "50%",
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "10px",
        fontWeight: 600,
        flexShrink: 0,
    },
    stepDotDone: {
        width: "16px",
        height: "16px",
        borderRadius: "50%",
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForegroundLink,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "10px",
        fontWeight: 600,
        flexShrink: 0,
    },
    stepConnector: {
        flex: 1,
        height: "1px",
        backgroundColor: tokens.colorNeutralStroke1,
        minWidth: "16px",
    },

    // ── Bulk-operation progress banner ─────────────────────────────────
    progressBanner: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px 16px",
        backgroundColor: tokens.colorBrandBackground2,
        borderBottom: `1px solid ${tokens.colorBrandStroke2}`,
        color: tokens.colorBrandForegroundLink,
    },
    progressBannerTitle: {
        fontSize: "13px",
        fontWeight: 600,
    },
    progressBannerDesc: {
        fontSize: "12px",
        color: tokens.colorNeutralForeground2,
    },
    disabledOverlay: {
        pointerEvents: "none",
        opacity: 0.5,
    },
});

// ── Column helper ─────────────────────────────────────────────────────────────

const columnHelper = createColumnHelper();

// ── Main Component ────────────────────────────────────────────────────────────

/**
 * LinkSalesforceOrgDrawer
 *
 * An overlay drawer for selecting and linking a Salesforce org to one or more queues.
 *
 * @param {object}   props
 * @param {boolean}  props.open        - Whether the drawer is open
 * @param {function} props.onDismiss   - Called when the drawer should close
 * @param {object[]} props.queues      - The queue rows being linked
 * @param {object[]} props.orgs        - Available Salesforce orgs
 * @param {boolean}  props.loading     - Whether orgs are being fetched
 * @param {function} props.onConfirm   - (orgId: string, queues: object[]) => void
 */
export default function LinkSalesforceOrgDrawer({
    open,
    onDismiss,
    queues = [],
    orgs = [],
    loading = false,
    linking = false,
    assigning = false,
    onConfirm,
    isGlobalSelectAll = false,
    totalCount = 0,
    tenantName,
    searchTenantId = undefined,
}) {
    const styles = useStyles();

    const [selectedOrgId, setSelectedOrgId] = useState(null);
    const [localFilter, setLocalFilter] = useState("");
    const [sorting, setSorting] = useState([]);

    // ── Step 2 — Missed Calls User state ──────────────────────────────────
    const [step, setStep] = useState("org"); // "org" | "user"
    const [selectedUser, setSelectedUser] = useState(null);
    const [userLocalFilter, setUserLocalFilter] = useState("");

    // Reset state when drawer is closed
    useEffect(() => {
        if (!open) {
            setSelectedOrgId(null);
            setLocalFilter("");
            setSorting([]);
            setStep("org");
            setSelectedUser(null);
            setUserLocalFilter("");
        }
    }, [open]);

    // ── Radio selection handler (step 1) ───────────────────────────────────
    // Clear the previously selected user whenever the org changes so it's
    // impossible to confirm with a user that belongs to a different org.
    const handleSelect = useCallback((orgId) => {
        setSelectedOrgId((prev) => {
            if (prev !== orgId) setSelectedUser(null);
            return orgId;
        });
    }, []);


    // ── Column definitions ─────────────────────────────────────────────────
    const columns = useMemo(
        () => [
            // Radio / single-select column
            columnHelper.display({
                id: "select",
                size: 44,
                enableSorting: false,
                header: () => null,
                cell: ({ row }) => (
                    <div className={styles.radioCell}>
                        <Radio
                            checked={selectedOrgId === row.original.id}
                            onChange={() => handleSelect(row.original.id)}
                            value={row.original.id}
                            aria-label={`Select Salesforce org ${row.original.orgId}`}
                        />
                    </div>
                ),
            }),

            // Org ID column
            columnHelper.accessor("orgId", {
                header: "Org ID",
                sortingFn: "alphanumeric",
                cell: (info) => (
                    <strong style={{ wordBreak: "break-all", fontFamily: "monospace", fontSize: "13px" }}>
                        {info.getValue()}
                    </strong>
                ),
            }),

            // Instance URL column
            columnHelper.accessor("instanceUrl", {
                header: "Instance URL",
                sortingFn: "alphanumeric",
                cell: (info) => {
                    const url = info.getValue();
                    return url ? (
                        <span style={{ fontSize: "12px", wordBreak: "break-all" }}>{url}</span>
                    ) : (
                        <span style={{ opacity: 0.45 }}>—</span>
                    );
                },
            }),

            // Status column — same Badge stack as SfOrgsTable
            columnHelper.display({
                id: "status",
                header: "Status",
                enableSorting: false,
                cell: ({ row }) => {
                    const r = row.original;
                    return (
                        <div style={{ display: "inline-flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                            {r.isSyncAuthenticated ? (
                                <Badge appearance="filled" color="success">Sync</Badge>
                            ) : r.isSyncEnabled ? (
                                <Badge appearance="outline" color="warning">Sync pending</Badge>
                            ) : null}
                            {r.isScvAuthenticated ? (
                                <Badge appearance="filled" color="success">SCV</Badge>
                            ) : r.isScvEnabled ? (
                                <Badge appearance="outline" color="warning">SCV pending</Badge>
                            ) : null}
                            {!r.isSyncEnabled && !r.isScvEnabled && (
                                <Badge appearance="ghost">Not linked</Badge>
                            )}
                        </div>
                    );
                },
            }),
        ],
        // columns intentionally re-memoized when selectedOrgId changes (radio state)
        [selectedOrgId, handleSelect],
    );

    // ── Client-side filtering (orgId only, case-insensitive, instant) ──────
    const filteredOrgs = useMemo(() => {
        const q = localFilter.trim().toLowerCase();
        if (!q) return orgs;
        return orgs.filter(
            (o) =>
                (o.orgId ?? "").toLowerCase().includes(q) ||
                (o.instanceUrl ?? "").toLowerCase().includes(q),
        );
    }, [orgs, localFilter]);

    // ── TanStack Table instance (step 1) ────────────────────────────────────
    const table = useReactTable({
        data: filteredOrgs,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getRowId: (row) => row.id,
    });


    // ── Org → SF Org ID lookup for step 2 user picker ─────────────────────
    const step2SfOrgId = useMemo(
        () => orgs.find((o) => o.id === selectedOrgId)?.orgId ?? null,
        [orgs, selectedOrgId],
    );

    // ── Confirm handler ────────────────────────────────────────────────────
    // Both an org AND a missed-call owner are required before the admin can
    // link the Salesforce organization to the selected queue(s).
    const handleConfirm = useCallback(() => {
        if (!selectedOrgId || !selectedUser?.sfUserId) return;
        onConfirm?.(selectedOrgId, queues, selectedUser);
    }, [selectedOrgId, selectedUser, queues, onConfirm]);

    // ── Drawer dismiss (blocked while any op is in flight) ───────────────────
    const busy = linking || assigning;
    const handleOpenChange = useCallback((_, { open: isOpen }) => {
        if (busy) return;
        if (!isOpen) onDismiss();
    }, [onDismiss, busy]);

    // ── Derived labels ─────────────────────────────────────────────────────
    const queueLabel = isGlobalSelectAll
        ? `${totalCount} queues`
        : queues.length === 1
            ? queues[0]?.queueName ?? "this queue"
            : `${queues.length} queues`;

    return (
        <OverlayDrawer
            open={open}
            onOpenChange={handleOpenChange}
            position="end"
            size="medium"
        >
            {/* ── Header ──────────────────────────────────────────────────── */}
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance="subtle"
                            aria-label="Close drawer"
                            icon={<Dismiss16Regular />}
                            onClick={onDismiss}
                            disabled={busy}
                        />
                    }
                >
                    Link Salesforce Org{tenantName ? ` : ${tenantName}` : ""}
                </DrawerHeaderTitle>
            </DrawerHeader>

            {/* ── Body ────────────────────────────────────────────────────── */}
            {/*
                Override DrawerBody defaults so we can control scroll ourselves:
                subtitle + search are fixed; the table area scrolls independently.
            */}
            <DrawerBody style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>

                {/* Bulk-operation progress banner — shown while link or assign is in flight */}
                {busy && (
                    <div className={styles.progressBanner} role="status" aria-live="polite">
                        <Spinner size="tiny" />
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span className={styles.progressBannerTitle}>
                                {linking
                                    ? "Linking Salesforce org to queues..."
                                    : "Assigning missed call owner..."}
                            </span>
                            <span className={styles.progressBannerDesc}>
                                {isGlobalSelectAll
                                    ? "Bulk operation in progress. This may take a moment for large tenants."
                                    : `Applying to ${queues.length} queue${queues.length === 1 ? "" : "s"}. Please don't close this window.`}
                            </span>
                        </div>
                    </div>
                )}

                {/* Top bar: step indicator + context label + search */}
                <div
                    className={busy ? styles.disabledOverlay : undefined}
                    style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
                >
                <div className={styles.topBar}>
                    {/* Step indicator */}
                    <div className={styles.stepBar}>
                        <span className={step === "org" ? styles.stepItemActive : styles.stepItemDone}>
                            <span className={step === "org" ? styles.stepDotActive : styles.stepDotDone}>1</span>
                            Select Org
                        </span>
                        <span className={styles.stepConnector} />
                        <span className={step === "user" ? styles.stepItemActive : styles.stepItem}>
                            <span className={step === "user" ? styles.stepDotActive : styles.stepDot}>2</span>
                            Assign Missed Call Owner
                        </span>
                    </div>

                    <Text className={styles.topBarLabel}>
                        {step === "org" ? (
                            <>
                                Linking to:{" "}
                                <strong style={{ color: tokens.colorNeutralForeground1 }}>{queueLabel}</strong>
                            </>
                        ) : (
                            <>
                                <PersonAdd16Regular style={{ verticalAlign: "middle", marginRight: 4 }} />
                                Assigning missed call owner for:{" "}
                                <strong style={{ color: tokens.colorNeutralForeground1 }}>{queueLabel}</strong>
                                <span style={{ marginLeft: 8, color: tokens.colorPaletteRedForeground1, fontWeight: 600 }}>
                                    · Required
                                </span>
                            </>
                        )}
                    </Text>
                    <GlobalTableSearch
                        value={step === "org" ? localFilter : userLocalFilter}
                        onChange={step === "org" ? setLocalFilter : setUserLocalFilter}
                        placeholder={
                            step === "org"
                                ? "Search by Org ID or Instance URL..."
                                : "Search by name, username, or Email..."
                        }
                        debounceMs={0}
                    />
                </div>

                {/* Content area — step 1: org · step 2: missed calls user */}
                {step === "org" ? (
                    loading ? (
                        // ── Loading state ─────────────────────────────────────
                        <div className={styles.stateCenter}>
                            <Spinner size="medium" />
                            <Text>Loading Salesforce organizations...</Text>
                        </div>
                    ) : orgs.length === 0 ? (
                        // ── Empty state ───────────────────────────────────────
                        <div className={styles.stateCenter}>
                            <Building16Regular
                                className={styles.stateIcon}
                                style={{ width: 40, height: 40 }}
                            />
                            <Text className={styles.stateTitle}>
                                No Salesforce Orgs Found
                            </Text>
                            <Text className={styles.stateDesc}>
                                Connect a Salesforce org before linking it to a queue.
                            </Text>
                        </div>
                    ) : (
                        // ── Org table ─────────────────────────────────────────
                        <div className={styles.tableScroll}>
                            <table className={styles.table}>
                                <thead className={styles.tableHeader}>
                                    {table.getHeaderGroups().map((hg) => (
                                        <tr key={hg.id}>
                                            {hg.headers.map((header) => (
                                                <th
                                                    key={header.id}
                                                    style={{
                                                        width: header.column.columnDef.size
                                                            ? header.column.columnDef.size
                                                            : undefined,
                                                        cursor: header.column.getCanSort()
                                                            ? "pointer"
                                                            : "default",
                                                    }}
                                                    onClick={(e) =>
                                                        header.column.getToggleSortingHandler()?.(e)
                                                    }
                                                >
                                                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext(),
                                                        )}
                                                        {header.column.getCanSort() && (
                                                            <Tooltip
                                                                content={
                                                                    header.column.getIsSorted()
                                                                        ? `Sorted ${
                                                                              header.column.getIsSorted() === "asc"
                                                                                  ? "ascending"
                                                                                  : "descending"
                                                                          }.`
                                                                        : "Click to sort."
                                                                }
                                                                relationship="label"
                                                            >
                                                                <span style={{ display: "inline-flex", alignItems: "center", marginLeft: 4 }}>
                                                                    {header.column.getIsSorted() === "asc" && <ArrowUp16Regular />}
                                                                    {header.column.getIsSorted() === "desc" && <ArrowDown16Regular />}
                                                                    {!header.column.getIsSorted() && <ArrowSort16Regular />}
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.length > 0 ? (
                                        table.getRowModel().rows.map((row) => (
                                            <tr
                                                key={row.id}
                                                className={
                                                    selectedOrgId === row.original.id
                                                        ? styles.rowSelected
                                                        : styles.row
                                                }
                                                onClick={() => handleSelect(row.original.id)}
                                            >
                                                {row.getVisibleCells().map((cell) => (
                                                    <td key={cell.id}>
                                                        {flexRender(
                                                            cell.column.columnDef.cell,
                                                            cell.getContext(),
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={columns.length}
                                                style={{ padding: "40px 16px", textAlign: "center" }}
                                            >
                                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                                                    <Text style={{ fontWeight: 600, color: tokens.colorNeutralForeground2 }}>No results found</Text>
                                                    <Text style={{ fontSize: "13px", color: tokens.colorNeutralForeground3 }}>Try a different Org ID.</Text>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )
                ) : (
                    // ── Step 2: Missed Calls User assignment
                    <SfUserPickerTable
                        sfOrgId={step2SfOrgId}
                        selectedUserId={selectedUser?.sfUserId ?? null}
                        onSelect={(user) => setSelectedUser(user)}
                        filter={userLocalFilter}
                        searchTenantId={searchTenantId}
                    />
                )}
                </div>
            </DrawerBody>

            {/* ── Footer ──────────────────────────────────────────────────── */}
            <DrawerFooter>
                <div className={styles.footerActions}>
                    {step === "org" ? (
                        <>
                            <Button appearance="subtle" onClick={onDismiss} disabled={busy}>
                                Cancel
                            </Button>
                            <Tooltip
                                content="Select a Salesforce org to continue."
                                relationship="label"
                                visible={!selectedOrgId ? undefined : false}
                            >
                                <span>
                                    <Button
                                        appearance="primary"
                                        icon={<ArrowRight16Regular />}
                                        disabled={!selectedOrgId || busy}
                                        onClick={() => setStep("user")}
                                    >
                                        Next: Assign Owner
                                    </Button>
                                </span>
                            </Tooltip>
                        </>
                    ) : (
                        <>
                            <Button
                                appearance="subtle"
                                onClick={() => setStep("org")}
                                disabled={busy}
                            >
                                ← Back
                            </Button>
                            <Tooltip
                                content="Select a missed call owner to link the Salesforce org."
                                relationship="label"
                                visible={!selectedUser?.sfUserId && !busy ? undefined : false}
                            >
                                <span>
                                    <Button
                                        appearance="primary"
                                        icon={busy ? <Spinner size="tiny" /> : <Link16Regular />}
                                        disabled={busy || !selectedUser?.sfUserId}
                                        onClick={handleConfirm}
                                    >
                                        {linking
                                            ? "Linking org..."
                                            : assigning
                                                ? "Assigning owner..."
                                                : "Link & Assign"}
                                    </Button>
                                </span>
                            </Tooltip>
                        </>
                    )}
                </div>
            </DrawerFooter>
        </OverlayDrawer>
    );
}
